import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { ShoppingCart, Package, Users, MailQuestion, IndianRupee } from "lucide-react";
import api from "../../lib/api";

export default function Dashboard() {
  const [stats, setStats] = useState(null);

  useEffect(() => {
    api.get("/admin/dashboard").then((r) => setStats(r.data)).catch(() => {});
  }, []);

  if (!stats) return <div className="h-40 bg-muted animate-pulse" />;

  const cards = [
    { label: "Revenue", value: `₹${stats.revenue.toLocaleString("en-IN")}`, icon: IndianRupee, testid: "stat-revenue" },
    { label: "Total Orders", value: stats.total_orders, icon: ShoppingCart, testid: "stat-orders" },
    { label: "Pending", value: stats.pending_orders, icon: ShoppingCart, testid: "stat-pending" },
    { label: "Confirmed", value: stats.confirmed_orders, icon: ShoppingCart, testid: "stat-confirmed" },
    { label: "Shipped", value: stats.shipped_status_orders, icon: ShoppingCart, testid: "stat-shipped" },
    { label: "Delivered", value: stats.delivered_orders, icon: ShoppingCart, testid: "stat-delivered" },
    { label: "Cancelled", value: stats.cancelled_orders, icon: ShoppingCart, testid: "stat-cancelled" },
    { label: "Products", value: stats.total_products, icon: Package, testid: "stat-products" },
    { label: "Customers", value: stats.total_customers, icon: Users, testid: "stat-customers" },
    { label: "New Enquiries", value: stats.new_enquiries, icon: MailQuestion, testid: "stat-enquiries" },
  ];

  return (
    <div data-testid="admin-dashboard">
      <h1 className="font-serif text-3xl font-semibold mb-8">Dashboard</h1>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10">
        {cards.map((c) => (
          <div key={c.label} className="bg-white border border-black/10 p-5" data-testid={c.testid}>
            <c.icon strokeWidth={1.5} size={20} className="text-gold" />
            <p className="font-serif text-2xl font-semibold mt-3">{c.value}</p>
            <p className="text-xs text-muted-foreground mt-1">{c.label}</p>
          </div>
        ))}
      </div>

      <div className="grid md:grid-cols-2 gap-4 mb-10">
        <div className="bg-white border border-black/10 p-5" data-testid="integration-health">
          <p className="small-caps text-gold mb-4">Integration Health</p>
          <div className="flex flex-col gap-2 text-sm">
            <p>Razorpay: {stats.integrations.razorpay_configured ? <span className="text-green-700">Connected ({stats.integrations.razorpay_mode})</span> : <span className="text-destructive">Not configured</span>}</p>
            <p>Shiprocket: {stats.integrations.shiprocket_configured ? <span className="text-green-700">Connected</span> : <span className="text-destructive">Not configured</span>}</p>
            <p>Email: {stats.integrations.email_configured ? <span className="text-green-700">Active</span> : <span className="text-destructive">Not configured</span>}</p>
            <p className="text-xs text-muted-foreground">Paid orders: {stats.paid_orders} · Shipments created: {stats.shipped_orders}</p>
          </div>
        </div>
        <div className="bg-white border border-black/10 p-5" data-testid="low-stock">
          <p className="small-caps text-gold mb-4">Low Stock (≤ 5 left)</p>
          {stats.low_stock.length === 0 ? (
            <p className="text-sm text-muted-foreground">All products well stocked.</p>
          ) : (
            <div className="flex flex-col gap-2 text-sm">
              {stats.low_stock.map((p) => (
                <div key={p.id} className="flex justify-between" data-testid={`low-stock-${p.id}`}>
                  <span className="truncate">{p.name}</span>
                  <span className={p.stock === 0 ? "text-destructive font-medium" : "text-gold"}>{p.stock} left</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="flex items-center justify-between mb-4">
        <h2 className="font-serif text-xl font-semibold">Recent Orders</h2>
        <Link to="/admin/orders" className="text-xs uppercase tracking-widest text-gold border-b border-gold" data-testid="view-all-orders">View All</Link>
      </div>
      {stats.recent_orders.length === 0 ? (
        <p className="text-sm text-muted-foreground bg-white border border-black/10 p-8 text-center" data-testid="no-orders-yet">No orders yet. They will appear here.</p>
      ) : (
        <div className="bg-white border border-black/10 divide-y divide-black/5">
          {stats.recent_orders.map((o) => (
            <div key={o.id} className="flex flex-wrap justify-between gap-2 p-4 text-sm" data-testid={`recent-order-${o.order_number}`}>
              <span className="font-medium">{o.order_number}</span>
              <span className="text-muted-foreground">{o.customer.name}</span>
              <span>₹{o.total.toLocaleString("en-IN")}</span>
              <span className="text-xs uppercase tracking-widest text-gold">{o.status}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
